import type { ItinerarySuccess } from "./api-types";
import type { PlannerPreferences } from "./preferences";
import { derivePreferences, DIFFICULTY_RANK } from "./preferences";
import { LIFT_STATUS_FRESH_MS } from "./lift-status/latest";

export type RouteTrust = {
  fit: { label: string; detail: string };
  conditions: { label: string; detail: string };
  evidence: { label: string; detail: string; dates: string[] };
};

function formatEvidenceDate(value: string | null | undefined, timeZone: string): string | null {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  try {
    return new Intl.DateTimeFormat("en-GB", { timeZone, day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
  } catch { return null; }
}

function counts(reported: number | undefined, used: number | undefined): string | null {
  if (!Number.isInteger(reported) || !Number.isInteger(used) || reported! < 0 || used! < 0 || reported! > used!) return null;
  return `${reported} of ${used}`;
}

/** Descriptive dimensions, never an invented probability of liking a route. */
export function routeTrust(plan: ItinerarySuccess, preferences?: PlannerPreferences, nowMs = Date.now()): RouteTrust {
  const descents = plan.itinerary.legs.filter(leg => leg.kind === "trail" && !leg.climb);
  const scores = descents.flatMap(leg => {
    const score = leg.kind !== "stop" ? leg.trail?.score : null;
    return score != null && Number.isFinite(score) && score >= 0 && score <= 10 ? [Math.round(score * 10)] : [];
  });
  const unrated = descents.some(leg => leg.kind !== "stop" && !leg.trail?.difficulty);
  const prefs = plan.preferences ?? preferences;
  const maxRank = prefs ? derivePreferences(prefs).maxRank : null;
  const hard = descents.some(leg => leg.kind !== "stop" && leg.trail?.difficulty && maxRank != null && DIFFICULTY_RANK[leg.trail.difficulty] > maxRank);
  const generatedMs = plan.generatedAt ? Date.parse(plan.generatedAt) : NaN;
  let today: string | null = null;
  try { today = new Intl.DateTimeFormat("en-CA", { timeZone: plan.area.timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(nowMs); } catch { /* Invalid old snapshot receives no freshness claim. */ }
  const oldDay = today !== plan.scoredDate;
  const oldSnapshot = !Number.isFinite(generatedMs) || generatedMs > nowMs + 60_000 || nowMs - generatedMs > LIFT_STATUS_FRESH_MS;
  const reportMs = plan.evidence?.oldestLiftStatusAt ? Date.parse(plan.evidence.oldestLiftStatusAt) : NaN;
  const oldLift = Number.isFinite(reportMs) && (reportMs > nowMs || nowMs - reportMs > LIFT_STATUS_FRESH_MS);
  const liftCounts = counts(plan.evidence?.reportedLiftCount, plan.evidence?.usedLiftCount);
  const trailCounts = counts(plan.evidence?.reportedTrailCount, plan.evidence?.usedTrailCount);
  const fullyReported = liftCounts !== null && trailCounts !== null && plan.evidence?.usedLiftCount === plan.evidence?.reportedLiftCount && plan.evidence?.usedTrailCount === plan.evidence?.reportedTrailCount;
  const dates = [
    ["Route checked", plan.generatedAt],
    ["Map access checked", plan.evidence?.mapAccessCheckedAt],
    ["Oldest lift report", plan.evidence?.oldestLiftStatusAt],
    ["Oldest trail bulletin", plan.evidence?.oldestTrailStatusAt],
  ].flatMap(([label, value]) => { const date = formatEvidenceDate(value, plan.area.timezone); return date ? [`${label}: ${date} (resort time).`] : []; });
  const coverage = [liftCounts ? `${liftCounts} lifts` : null, trailCounts ? `${trailCounts} trails` : null].filter(Boolean).join(" and ");
  return {
    fit: {
      label: hard ? "Review the hardest section" : unrated ? "Some terrain is unrated" : prefs ? "Based on your settings" : "Saved route preferences",
      detail: hard ? "A section is harder than your selected ability. Check your settings before using this route." : "The route balances your terrain, effort and time choices. Your hardest section and chosen finish are shown above.",
    },
    conditions: {
      label: !scores.length ? "Run forecasts unavailable" : `${scores.length} of ${descents.length} descents scored`,
      detail: scores.length ? `Expected run scores ${Math.min(...scores)}${Math.min(...scores) === Math.max(...scores) ? "" : ` to ${Math.max(...scores)}`}/100 at the planned times on ${plan.scoredDate}. These assess expected conditions, not the chance you will enjoy the route.${oldDay ? " This is a different day from today." : ""}` : "Unscored sections use mapped terrain and your preferences. No conditions score has been supplied for them.",
    },
    evidence: {
      label: oldDay || oldSnapshot || oldLift ? "Refresh before setting off" : plan.preview || !fullyReported || plan.routeExplanation.confidence !== "high" ? "Some evidence is limited" : "Reports support this route",
      detail: coverage ? `When generated, ${coverage} had reported opening. ${oldDay || oldSnapshot || oldLift ? "Saved reports do not establish current opening." : "Opening can change after a report."}` : "This saved route does not include a full report-coverage count. Review the data caveats and operator information.",
      dates,
    },
  };
}
